/**
 * Отправка событий аналитики в Umami через наш домен (I3). Кроме событий — один просмотр страницы на открытие игры
 * ({@link Analytics.pageview}): без него в Umami пусты Visitors/Visits/Views, источники, страны и UTM. Без id сайта (`/u/site.json` отвечает 404) и без сети модуль
 * молчит: игра от аналитики не зависит. Имена и свойства событий — `AnalyticsEvent` из контрактов, личных данных нет.
 * События до получения id ждут в очереди (не больше {@link MAX_QUEUE}); неудачная отправка повторяется не больше
 * {@link MAX_ATTEMPTS} раз — при следующем событии или возвращении сети.
 */
import type { AnalyticsEvent } from "@newfungame/contracts";
import { externalReferrer } from "../app/landing";

export const SEND_PATH = "/u/api/send";
export const SITE_PATH = "/u/site.json";
export const MAX_QUEUE = 100;
export const MAX_ATTEMPTS = 3;

/** Контекст страницы для Umami (его трекер собирает то же самое). */
export interface PageContext {
  hostname: string;
  url: string;
  referrer: string;
  language: string;
  screen: string;
  title: string;
}

export interface AnalyticsDeps {
  fetch: typeof fetch;
  /** Id сайта в Umami; `null` — аналитика выключена. Вызывается один раз. */
  website(): Promise<string | null>;
  page(): PageContext;
  /**
   * Свойства, общие для всех событий (рука A/B-теста масштаба поля `scale`, MADR 0036); берутся в момент
   * `track`. Свойства самого события важнее.
   */
  common?(): Record<string, string | number | boolean | null>;
  /** Umami принял отправку и выдал токен визита (`cache`) — по нему рекордер вешает запись на этот визит. */
  onCache?(cache: string): void;
}

/** Что уходит в Umami: просмотр страницы (`payload` без `name`) или событие. */
export type Hit = { kind: "pageview" } | { kind: "event"; event: AnalyticsEvent };

export interface Analytics {
  track(event: AnalyticsEvent): void;
  /** Просмотр страницы: один на открытие игры, внутренние переходы его не повторяют. */
  pageview(): void;
  /** То же, что `track`/`pageview`, одним входом — для ворот и проверки тестового аккаунта. */
  hit(hit: Hit): void;
  /** Токен визита из последнего ответа Umami; `null` — ещё ничего не принято. Однажды полученный не обнуляется. */
  cache(): string | null;
  /** Вернулась сеть: повторить отправку того, что не ушло. */
  retry(): void;
  /** Все поставленные события обработаны (отправлены, отброшены или аналитика выключена) — для тестов. */
  idle(): Promise<void>;
}

interface Queued {
  hit: Hit;
  common: Record<string, unknown>;
  attempts: number;
}

/** Свойства без `null` (Umami принимает только значения). */
function cleanProps(props: Record<string, unknown>): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(props))
    if (v !== null && v !== undefined) out[k] = v as string | number | boolean;
  return out;
}

export function createAnalytics(deps: AnalyticsDeps): Analytics {
  let queue: Queued[] = [];
  let website: string | null | undefined; // undefined — ещё не узнали
  let websiteLoad: Promise<void> | null = null;
  let running: Promise<void> = Promise.resolve();
  let cache: string | null = null;

  const resolveWebsite = (): Promise<void> => {
    websiteLoad ??= deps.website().then(
      (id) => {
        website = id;
      },
      () => {
        website = null;
      },
    );
    return websiteLoad;
  };

  const send = async (id: string, item: Queued): Promise<boolean> => {
    const { hit } = item;
    const payload =
      hit.kind === "pageview"
        ? { website: id, ...deps.page() }
        : {
            website: id,
            ...deps.page(),
            name: hit.event.name,
            data: cleanProps({ ...item.common, ...hit.event.props }),
          };
    try {
      const res = await deps.fetch(SEND_PATH, {
        method: "POST",
        headers: { "content-type": "application/json", ...(cache && { "x-umami-cache": cache }) },
        keepalive: true,
        body: JSON.stringify({ type: "event", payload }),
      });
      if (res.ok) await readCache(res);
      return res.ok;
    } catch {
      return false;
    }
  };

  /** Токен из ответа `{cache}`. Пустой ответ старый токен не стирает: рекордер без него молча теряет кусок записи. */
  const readCache = async (res: Response): Promise<void> => {
    try {
      const body = (await res.json()) as { cache?: unknown };
      if (typeof body.cache !== "string" || !body.cache || body.cache === cache) return;
      cache = body.cache;
      deps.onCache?.(cache);
    } catch {
      /* ответ не JSON — токена нет */
    }
  };

  const drain = async (): Promise<void> => {
    await resolveWebsite();
    if (!website) {
      queue = [];
      return;
    }
    const batch = queue;
    queue = [];
    const failed: Queued[] = [];
    for (const item of batch) {
      if (await send(website, item)) continue;
      if (++item.attempts < MAX_ATTEMPTS) failed.push(item);
    }
    // за время отправки могли встать новые события — порядок сохраняем
    queue = [...failed, ...queue];
  };

  const run = (): void => {
    running = running.then(drain);
  };

  const hit = (h: Hit): void => {
    if (website === null) return;
    if (queue.length >= MAX_QUEUE) queue.shift();
    queue.push({ hit: h, common: deps.common?.() ?? {}, attempts: 0 });
    run();
  };

  return {
    track: (event) => hit({ kind: "event", event }),
    pageview: () => hit({ kind: "pageview" }),
    hit,
    cache: () => cache,
    retry() {
      if (website && queue.length > 0) run();
    },
    idle: () => running,
  };
}

/** Id сайта с нашего сервера; любой сбой и пустой ответ — `null`. */
export async function fetchWebsiteId(fetchImpl: typeof fetch = fetch): Promise<string | null> {
  try {
    const res = await fetchImpl(SITE_PATH, { headers: { accept: "application/json" } });
    if (!res.ok || !(res.headers.get("content-type") ?? "").includes("json")) return null;
    const body = (await res.json()) as { websiteId?: unknown };
    return typeof body.websiteId === "string" && body.websiteId ? body.websiteId : null;
  } catch {
    return null;
  }
}

/** `utm_source` по метке `startapp`: реклама и приглашения узнаются по началу метки. */
export function startParamSource(startParam: string): string | null {
  if (startParam.startsWith("adsgram")) return "adsgram";
  if (startParam.startsWith("tgads")) return "telegram-ads";
  if (startParam.startsWith("fr_")) return "telegram-friend";
  if (startParam.startsWith("sh_")) return "telegram-share";
  return null;
}

/**
 * Адрес для Umami: UTM он разбирает сам, остальной query (служебные метки Telegram, параметры отладки) не нужен.
 * Метка `startapp` Mini App в адрес не попадает (она в `tgWebAppStartParam` или только в initData) — её кладём в
 * `utm_content`, а для рекламы и приглашений ещё и `utm_source`: Main Mini App всегда открывается с `utm_source=telegram`.
 */
export function trackedUrl(pathname: string, search: string, startParam: string | null = null): string {
  const query = new URLSearchParams();
  for (const [key, value] of new URLSearchParams(search)) if (key.startsWith("utm_")) query.append(key, value);
  if (startParam) {
    const source = startParamSource(startParam);
    if (source) query.set("utm_source", source);
    else if (!query.has("utm_source")) query.set("utm_source", "telegram");
    query.set("utm_content", startParam);
  }
  const tail = query.toString();
  return tail ? `${pathname}?${tail}` : pathname;
}

/** Проверка на проде (`utm_source=qa-*`): в статистику не попадает, как в Sapero. */
export function isQaVisit(search: string): boolean {
  return new URLSearchParams(search).get("utm_source")?.startsWith("qa-") ?? false;
}

export function browserPageContext(startParam: string | null = null): PageContext {
  const referrer = externalReferrer(document.referrer, location.hostname);
  return {
    hostname: location.hostname,
    url: trackedUrl(location.pathname, location.search, startParam),
    referrer: referrer ? `https://${referrer}/` : "",
    language: navigator.language,
    screen: `${screen.width}x${screen.height}`,
    title: document.title,
  };
}
